import Breadcrumb from "components/Common/Breadcrumb"
import TableContainer from "components/Common/DataTableContainer"
import DashboardLoader from "components/Loader/DashboardLoader"
import DeleteModal from "pages/Calendar/DeleteModal"
import React, { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import {
  useGetAllUsersQuery,
  useDeleteUserMutation,
} from "store/features/users/userManagementApi"
import EditUserModal from "components/Modals/EditUserModal"
import QwikITUserDetailsModal from "components/Modals/QwikITUserDetailsModal"

const roleFormatMap = {
  admin: { color: "danger", text: "Admin" },
  super_admin: { color: "dark", text: "Super Admin" },
  project_manager: { color: "warning", text: "Project Manager" },
  developer: { color: "primary", text: "Developer" },
  designer: { color: "info", text: "Designer" },
  marketing: { color: "success", text: "Marketing" },
  hr: { color: "purple", text: "HR" },
  business_operation: { color: "info", text: "Business Operation" },
  client: { color: "secondary", text: "Client" },
}

const getRoleConfig = role => {
  if (!role) return { color: "secondary", text: "N/A" }
  const clean = String(role).toLowerCase().trim().replace(/[-\s]/g, "_")
  if (roleFormatMap[clean]) return roleFormatMap[clean]
  const formatted = clean
    .split("_")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
  return { color: "primary", text: formatted }
}

const AllUsers = () => {
  const { data: users, isLoading, refetch } = useGetAllUsersQuery()
  const [deleteUser, { isLoading: deletingUser }] = useDeleteUserMutation()

  const [deleteModal, setDeleteModal] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [detailsModalOpen, setDetailsModalOpen] = useState(false)
  const [selectedUserForDetails, setSelectedUserForDetails] = useState(null)
  const [roleFilter, setRoleFilter] = useState("")

  const showDeleteModal = id => {
    setSelectedUserId(id)
    setDeleteModal(true)
  }

  const handleDeleteUser = () => {
    deleteUser(selectedUserId)
    setDeleteModal(false)
  }

  const handleEditUser = user => {
    setSelectedUser(user)
    setEditModalOpen(true)
  }

  const handleViewDetails = user => {
    setSelectedUserForDetails(user)
    setDetailsModalOpen(true)
  }

  const handleUserUpdate = () => {
    refetch()
    setEditModalOpen(false)
  }

  // Filter users based on role
  const filteredUsers = useMemo(() => {
    if (!users) return []

    return users.filter(user => {
      if (!roleFilter) return true
      const cleanUserRole = String(user.role || "").toLowerCase().trim().replace(/[-\s]/g, "_")
      const cleanFilter = String(roleFilter).toLowerCase().trim().replace(/[-\s]/g, "_")
      return cleanUserRole === cleanFilter
    })
  }, [users, roleFilter])

  const columns = useMemo(
    () => [
      {
        header: "Image",
        accessorKey: "image",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ cursor: "pointer" }}
            title="Click to view details"
            onClick={() => handleViewDetails(row.original)}
          >
            {row.original.image ? (
              <img
                src={row.original.image}
                alt={row.original.nickname}
                className="rounded-circle shadow-sm"
                style={{ width: "42px", height: "42px", objectFit: "cover" }}
              />
            ) : (
              <i
                className="fas fa-user-circle text-secondary"
                style={{ fontSize: "42px" }}
              ></i>
            )}
          </div>
        ),
      },
      {
        header: "Name",
        accessorKey: "nickname",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div
            style={{ cursor: "pointer" }}
            className="fw-bold text-primary text-decoration-none"
            title="Click to view full details"
            onClick={() => handleViewDetails(row.original)}
          >
            {row.original.nickname || "N/A"}
            {row.original.emp_id && (
              <small className="d-block text-muted fw-normal">ID: {row.original.emp_id}</small>
            )}
          </div>
        ),
      },
      {
        header: "Role",
        accessorKey: "role",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const conf = getRoleConfig(row.original.role)
          return (
            <span className={`badge bg-${conf.color} px-2 py-1 fs-7 text-uppercase`}>
              {conf.text}
            </span>
          )
        },
      },
      {
        header: "Position",
        accessorKey: "position",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => row.original.position || "N/A",
      },
      {
        header: "Permission",
        accessorKey: "crm_permissions",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const permissions = row.original.crm_permissions

          if (!permissions || permissions.length === 0) return "N/A"

          const labelMap = {
            qwik_it: "Softzen IT",
            qwik_medic: "Qwik Medic",
            qwik_bistro: "Qwik Bistro",
          }

          let parsedPerms = []
          if (Array.isArray(permissions)) {
            parsedPerms = permissions
          } else if (typeof permissions === "string") {
            try {
              parsedPerms = JSON.parse(permissions)
            } catch {
              parsedPerms = permissions.split(",").map(p => p.trim())
            }
          }

          return (
            <ol style={{ paddingLeft: "18px", margin: 0 }}>
              {parsedPerms.map((crm, index) => (
                <li key={index}>{labelMap[crm] || crm}</li>
              ))}
            </ol>
          )
        },
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="d-flex align-items-center gap-1">
            <button
              onClick={() => handleViewDetails(row.original)}
              className="btn btn-info btn-sm"
              title="View Full Details"
            >
              <i className="fas fa-eye"></i>
            </button>
            <button
              onClick={() => handleEditUser(row.original)}
              className="btn btn-primary btn-sm"
              title="Edit User"
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => showDeleteModal(row.original.id)}
              title="Delete User"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    [deletingUser]
  )

  if (isLoading) {
    return <DashboardLoader />
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Users" breadcrumbItem="All Users" />

        <div className="mb-3 gap-5 d-flex justify-content-end align-items-center">
          <div className="d-flex gap-2 align-items-center">
            <div>
              <select
                className="form-select d-inline-block"
                style={{ width: "auto" }}
                value={roleFilter}
                onChange={e => setRoleFilter(e.target.value)}
              >
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="project_manager">Project Manager</option>
                <option value="developer">Developer</option>
                <option value="designer">Designer</option>
                <option value="marketing">Marketing</option>
                <option value="hr">HR</option>
              </select>
            </div>
          </div>

          <Link to="/create-user" className="btn btn-primary">
            <i className="fas fa-plus me-2"></i>Create User
          </Link>
        </div>

        <TableContainer
          columns={columns}
          data={filteredUsers}
          isGlobalFilter={true}
          isPagination={true}
          SearchPlaceholder="Search Users..."
          pagination="pagination"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteUser}
          loader={deletingUser}
          onCloseClick={() => setDeleteModal(false)}
        />

        {selectedUser && (
          <EditUserModal
            isOpen={editModalOpen}
            toggle={() => setEditModalOpen(false)}
            user={selectedUser}
            onUserUpdate={handleUserUpdate}
          />
        )}

        {selectedUserForDetails && (
          <QwikITUserDetailsModal
            isOpen={detailsModalOpen}
            toggle={() => setDetailsModalOpen(false)}
            user={selectedUserForDetails}
            onEditUser={userToEdit => {
              setSelectedUser(userToEdit)
              setEditModalOpen(true)
            }}
          />
        )}
      </div>
    </div>
  )
}

export default AllUsers
